import { useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Send, Loader2, X, Check, Search, Users } from "lucide-react";

import { api } from "../lib/api";
import type { EmployeeSummary } from "../lib/types";

type Audience = "all" | "country" | "nationality" | "silent_48h" | "selection";

interface BroadcastResponse {
  requested: number;
  sent: number;
  skipped_no_phone: number;
  skipped_send_failed: number;
}

interface Props {
  countries: string[];
  nationalities: string[];
  /** Full employee list visible on the dashboard, used for the "Selection"
   * mode picker. We deliberately use the already-loaded list rather than
   * re-fetching, so HR sees exactly the same set of people they're looking
   * at when they hit Broadcast. */
  employees: EmployeeSummary[];
}

export default function BroadcastButton({ countries, nationalities, employees }: Props) {
  const [open, setOpen] = useState(false);
  const [audience, setAudience] = useState<Audience>("all");
  const [country, setCountry] = useState("");
  const [nationality, setNationality] = useState("");
  const [message, setMessage] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");

  const send = useMutation({
    mutationFn: () =>
      api.post<BroadcastResponse>("/api/broadcasts/check-in", {
        audience,
        country: audience === "country" ? country : null,
        nationality: audience === "nationality" ? nationality : null,
        oracle_ids: audience === "selection" ? Array.from(selected) : [],
        custom_message: message || null,
      }),
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter((e) => {
      const hay = `${e.full_name} ${e.email ?? ""} ${e.oracle_id} ${e.title ?? ""} ${e.org_unit ?? ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [employees, search]);

  const noPhone = useMemo(() => {
    let n = 0;
    selected.forEach((id) => {
      const e = employees.find((x) => x.oracle_id === id);
      if (e && !e.primary_phone_masked) n += 1;
    });
    return n;
  }, [selected, employees]);

  function toggle(id: string) {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function reset() {
    send.reset();
    setOpen(false);
    setAudience("all");
    setCountry("");
    setNationality("");
    setMessage("");
    setSelected(new Set());
    setSearch("");
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-md bg-sky-500/15 hover:bg-sky-500/25 ring-1 ring-sky-400/30 text-sky-200 px-3 py-1.5 text-sm"
      >
        <Send size={14} /> Broadcast check-in
      </button>

      {open && (
        <div className="fixed inset-0 z-[1100] bg-black/60 flex items-end sm:items-center justify-center sm:p-4">
          <div className="w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-xl bg-[var(--color-surface)] ring-1 ring-[var(--color-border)] p-4 sm:p-5 space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-lg font-semibold text-slate-100">Broadcast check-in</div>
              <button onClick={reset} className="text-slate-400 hover:text-slate-200">
                <X size={18} />
              </button>
            </div>

            {!send.isSuccess && (
              <>
                <p className="text-sm text-slate-400">
                  Each recipient gets a personal one-time WhatsApp link. Submitted
                  replies update the dashboard in real time.
                </p>

                <label className="block">
                  <span className="text-xs uppercase tracking-wide text-slate-400">Audience</span>
                  <select
                    value={audience}
                    onChange={(e) => setAudience(e.target.value as Audience)}
                    className="mt-1 w-full rounded-md bg-slate-900/80 ring-1 ring-slate-700 px-3 py-2 text-sm text-slate-100"
                  >
                    <option value="all">Everyone (all employees)</option>
                    <option value="silent_48h">Only silent &gt; 48h</option>
                    <option value="country">By current country</option>
                    <option value="nationality">By nationality</option>
                    <option value="selection">Specific employees…</option>
                  </select>
                </label>

                {audience === "country" && (
                  <select
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    className="w-full rounded-md bg-slate-900/80 ring-1 ring-slate-700 px-3 py-2 text-sm text-slate-100"
                  >
                    <option value="">Select country…</option>
                    {countries.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                )}
                {audience === "nationality" && (
                  <select
                    value={nationality}
                    onChange={(e) => setNationality(e.target.value)}
                    className="w-full rounded-md bg-slate-900/80 ring-1 ring-slate-700 px-3 py-2 text-sm text-slate-100"
                  >
                    <option value="">Select nationality…</option>
                    {nationalities.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                )}

                {audience === "selection" && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs uppercase tracking-wide text-slate-400 inline-flex items-center gap-1.5">
                        <Users size={12} /> Recipients
                      </span>
                      <span className="text-xs text-slate-400">
                        {selected.size} selected
                        {noPhone > 0 && (
                          <span className="text-amber-300"> · {noPhone} without phone</span>
                        )}
                      </span>
                    </div>
                    <div className="relative">
                      <Search
                        size={14}
                        className="absolute left-2.5 top-2.5 text-slate-500"
                      />
                      <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search name, email, oracle id, unit…"
                        className="w-full pl-8 pr-3 py-2 text-sm rounded-md bg-slate-900/80 ring-1 ring-slate-700 text-slate-100 outline-none focus:ring-sky-500"
                      />
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <button
                        type="button"
                        onClick={() =>
                          setSelected(new Set(filtered.map((e) => e.oracle_id)))
                        }
                        className="px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 ring-1 ring-slate-700 text-slate-200"
                      >
                        Select all visible ({filtered.length})
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelected(new Set())}
                        className="px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 ring-1 ring-slate-700 text-slate-200"
                      >
                        Clear
                      </button>
                    </div>
                    <div className="max-h-72 overflow-y-auto rounded-md ring-1 ring-slate-800 divide-y divide-slate-800">
                      {filtered.length === 0 ? (
                        <div className="text-xs text-slate-500 px-3 py-3">
                          No employees match the search.
                        </div>
                      ) : (
                        filtered.map((e) => {
                          const isSel = selected.has(e.oracle_id);
                          return (
                            <button
                              key={e.oracle_id}
                              type="button"
                              onClick={() => toggle(e.oracle_id)}
                              className={
                                "w-full text-left px-3 py-1.5 flex items-center gap-3 hover:bg-slate-800/60 " +
                                (isSel ? "bg-sky-500/10" : "")
                              }
                            >
                              <span
                                className={
                                  "inline-flex items-center justify-center w-4 h-4 rounded border " +
                                  (isSel
                                    ? "bg-sky-500 border-sky-400 text-white"
                                    : "border-slate-600 text-transparent")
                                }
                              >
                                <Check size={11} />
                              </span>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm text-slate-100 truncate">
                                  {e.full_name}
                                </div>
                                <div className="text-[11px] text-slate-500 truncate">
                                  {e.title || e.org_unit || "—"}
                                  {e.primary_phone_masked
                                    ? ` · ${e.primary_phone_masked}`
                                    : " · no phone"}
                                </div>
                              </div>
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}

                <label className="block">
                  <span className="text-xs uppercase tracking-wide text-slate-400">
                    Custom message (optional)
                  </span>
                  <textarea
                    rows={3}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="e.g. Just checking in — please confirm where you are."
                    className="mt-1 w-full rounded-md bg-slate-900/80 ring-1 ring-slate-700 px-3 py-2 text-sm text-slate-100 resize-none"
                  />
                </label>

                <button
                  onClick={() => send.mutate()}
                  disabled={
                    send.isPending ||
                    (audience === "country" && !country) ||
                    (audience === "nationality" && !nationality) ||
                    (audience === "selection" && selected.size === 0)
                  }
                  className="w-full inline-flex items-center justify-center gap-1.5 rounded-md bg-sky-500 hover:bg-sky-400 disabled:opacity-50 text-white px-4 py-2 text-sm font-medium"
                >
                  {send.isPending ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
                  {audience === "selection"
                    ? `Send to ${selected.size} selected`
                    : "Send to selected"}
                </button>
                {send.isError && (
                  <div className="text-sm text-red-300">{(send.error as Error).message}</div>
                )}
              </>
            )}

            {send.isSuccess && (
              <div className="space-y-2">
                <div className="text-emerald-300">
                  Sent {send.data.sent} of {send.data.requested} messages.
                </div>
                <div className="text-xs text-slate-400">
                  Skipped — no phone: {send.data.skipped_no_phone} · failed:{" "}
                  {send.data.skipped_send_failed}
                </div>
                <button
                  onClick={reset}
                  className="w-full rounded-md bg-slate-800 hover:bg-slate-700 text-slate-200 px-4 py-2 text-sm"
                >
                  Done
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
